/**
 * SystemNotificationHost — mounted once at the App root.
 *
 * Responsibilities:
 *   1. On mount, fetch pending notifications the user hasn't answered.
 *   2. Subscribe to /notifications Socket.IO for:
 *        * system_notification_new    — new broadcast
 *        * system_notification_closed — admin retired one; dismiss
 *          the head modal if it matches (race: admin closes while
 *          user is answering).
 *   3. On every WS reconnect, re-fetch /pending — a push that landed
 *      while the socket was down is caught up. Post-review fix per
 *      owner-req #3.
 *   4. Heartbeat every 25s (HTTP fallback + WS event) so passive
 *      readers stay Online in the admin dashboard.
 *   5. Priority-critical items jump to the front of the queue on
 *      arrival; server-side pending endpoint sorts by severity too.
 *
 * Owner-spec (PR-F Phase 1): user cannot do ANYTHING in the app until
 * they respond to a blocking notification — enforced at the FE layer.
 * Backend gate (HTTP 423 Locked on mutating endpoints) is Phase 2.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import config from '../../config/config';
import SystemNotificationBlockingModal from './SystemNotificationBlockingModal';

const API_BASE = config.api.baseUrl;
const HEARTBEAT_INTERVAL_MS = 25_000;

const SystemNotificationHost = ({ sessionId, isAuthenticated }) => {
  const [queue, setQueue] = useState([]);
  const socketRef = useRef(null);
  const seenIdsRef = useRef(new Set());

  // Shared fetch — initial mount AND every WS reconnect.
  const refreshPending = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/api/system-notifications/pending`, {
        credentials: 'include',
        headers: sessionId ? { 'X-Session-Id': sessionId } : {},
      });
      if (!r.ok) return;
      const j = await r.json();
      const list = j.notifications || [];
      const fresh = list.filter(n => !seenIdsRef.current.has(n.id));
      fresh.forEach(n => seenIdsRef.current.add(n.id));
      if (fresh.length > 0) setQueue(prev => [...prev, ...fresh]);
    } catch (_) { /* WS reconnect will retry */ }
  }, [sessionId]);

  // (1) Initial fetch.
  useEffect(() => {
    if (!isAuthenticated) { setQueue([]); seenIdsRef.current = new Set(); return; }
    refreshPending();
  }, [sessionId, isAuthenticated, refreshPending]);

  // (2) Subscribe to /notifications for pushes + reconnect re-fetch.
  useEffect(() => {
    if (!isAuthenticated || !sessionId) return;

    let disposed = false;
    (async () => {
      try {
        const { afSocket } = await import('../../utils/socket');
        const socket = afSocket.connect('/notifications', sessionId);
        socketRef.current = socket;

        // New notification push. Critical ones jump to the FRONT.
        afSocket.on('/notifications', 'system_notification_new', (data) => {
          if (disposed || !data || !data.id) return;
          if (seenIdsRef.current.has(data.id)) return;
          seenIdsRef.current.add(data.id);
          setQueue(prev =>
            data.severity === 'critical' ? [data, ...prev] : [...prev, data]);
        });

        // Admin closed an active notification while a modal was up
        // (race guard). Drop the entry so the user isn't stuck on a
        // notification that no longer exists server-side.
        afSocket.on('/notifications', 'system_notification_closed', (data) => {
          if (disposed || !data || !data.notification_id) return;
          setQueue(prev => prev.filter(n => n.id !== data.notification_id));
          seenIdsRef.current.delete(data.notification_id);
        });

        // Reconnect handler — the underlying io socket exposes a raw
        // `.on('connect')` that fires on both initial + every reconnect.
        if (socket && typeof socket.on === 'function') {
          socket.on('connect', () => { if (!disposed) refreshPending(); });
        }
      } catch (e) {
        console.warn('[SystemNotificationHost] socket subscribe failed:', e);
      }
    })();
    return () => { disposed = true; };
  }, [sessionId, isAuthenticated, refreshPending]);

  // (4) Heartbeat — HTTP fallback + WS event.
  useEffect(() => {
    if (!isAuthenticated || !sessionId) return;

    const httpBeat = async () => {
      try {
        await fetch(`${API_BASE}/api/system-notifications/heartbeat`, {
          method: 'POST',
          credentials: 'include',
          headers: sessionId ? { 'X-Session-Id': sessionId } : {},
        });
      } catch (_) { /* fire-and-forget */ }
    };
    httpBeat();
    const httpTimer = setInterval(httpBeat, HEARTBEAT_INTERVAL_MS);

    let disposed = false;
    let wsTimer = null;
    (async () => {
      try {
        const { afSocket } = await import('../../utils/socket');
        const socket = afSocket.connect('/notifications', sessionId);
        const emitBeat = () => {
          try { socket && socket.emit && socket.emit('heartbeat', {}); } catch (_) {}
        };
        emitBeat();
        wsTimer = setInterval(() => { if (!disposed) emitBeat(); }, HEARTBEAT_INTERVAL_MS);
      } catch (_) { /* HTTP heartbeat still covers us */ }
    })();
    return () => {
      clearInterval(httpTimer);
      disposed = true;
      if (wsTimer) clearInterval(wsTimer);
    };
  }, [sessionId, isAuthenticated]);

  const handleResponded = () => {
    setQueue(prev => prev.slice(1));
  };

  if (!isAuthenticated || queue.length === 0) return null;
  const head = queue[0];

  return (
    <SystemNotificationBlockingModal
      key={head.id}
      notification={head}
      sessionId={sessionId}
      onResponded={handleResponded}
    />
  );
};

export default SystemNotificationHost;
